import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, Database, X, History, RefreshCw, SkipForward, Replace, Trash2, Filter, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';


import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/dates';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

interface UploadedFile {
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
}

interface DateIssue {
  row: number;
  field: string;
  rawValue: string;
  reason: string;
}

interface ParsedData {
  headers: string[];
  rows: Record<string, string>[];
  errors: string[];
  duplicates: Record<string, string>[];
  newRecords: Record<string, string>[];
  filteredOutCount: number;
  dateIssues: DateIssue[];
}


interface UploadHistoryRecord {
  id: number;
  created_at: string;
  upload_type: string;
  filename: string;
  record_count: number;
  duplicates_found: number;
  duplicates_skipped: number;
  duplicates_overwritten: number;
  status: string;
  error_message: string | null;
  uploaded_by: string | null;
  upload_batch_id: string | null;
}

// Expected headers for each report type
const EFTPOS_HEADERS = [
  'merchant_id', 'terminal_id', 'transaction_date', 'settlement_date', 
  'pan', 'txn_number', 'logo', 'type', 'transaction_amount', 'purchase', 'cash', 'status'
];

const RIDEWISE_HEADERS = [
  'driver_number', 'vehicle_identifier', 'transaction_type', 'region', 
  'voucher_ref_number', 'operator_transaction_ref', 'concession_type', 'trip_date',
  'pickup_location', 'dropoff_location', 'pickup_gis', 'dropoff_gis', 
  'start_time', 'end_time', 'fare_paid', 'concession_amount', 'fare_total',
  'number_of_passengers', 'kilometres', 'hoist_deployed', 'client_number',
  'client_surname', 'client_first_name', 'additional_information'
];

// Header mapping for EFTPOS (maps CSV header to expected header)
const EFTPOS_HEADER_MAPPING: Record<string, string> = {
  'merchant': 'merchant_id',
  'terminal': 'terminal_id',
  'trans_date': 'transaction_date',
  'settle_date': 'settlement_date',
  'txn': 'txn_number',
  'txn_#': 'txn_number',
  'txn#': 'txn_number',
  'transaction_number': 'txn_number',
  'amount': 'transaction_amount',
  'trans_amount': 'transaction_amount',
  'transaction_type': 'type', // EFTPOS uses 'type' not 'transaction_type'
};


// Header mapping for RideWise/Total Mobility
const RIDEWISE_HEADER_MAPPING: Record<string, string> = {
  'voucher_or_ref_number': 'voucher_ref_number',
  'voucher_ref': 'voucher_ref_number',
  'voucher_reference': 'voucher_ref_number',
  'voucher_number': 'voucher_ref_number',
  'driver': 'driver_number',
  'vehicle': 'vehicle_identifier',
  'vehicle_id': 'vehicle_identifier',
  'trans_type': 'transaction_type',
  'concession': 'concession_type',
  'date': 'trip_date',
  'pickup': 'pickup_location',
  'dropoff': 'dropoff_location',
  'drop_off_location': 'dropoff_location',
  'pick_up_location': 'pickup_location',
  'fare': 'fare_paid',
  'total': 'fare_total',
  'passengers': 'number_of_passengers',
  'num_passengers': 'number_of_passengers',
  'no_of_passengers': 'number_of_passengers',
  'passenger_count': 'number_of_passengers',
  'pax': 'number_of_passengers',
  'km': 'kilometres',
  'kms': 'kilometres',
  'kilometers': 'kilometres',
  'distance': 'kilometres',
  'hoist': 'hoist_deployed',
  'client': 'client_number',
  'surname': 'client_surname',
  'first_name': 'client_first_name',
  'firstname': 'client_first_name',
  'additional_info': 'additional_information',
  'notes': 'additional_information',
};

// Columns to show for each type in the delete records dialog
const EFTPOS_DISPLAY_COLUMNS = ['id', 'transaction_date', 'terminal_id', 'txn_number', 'logo', 'type', 'transaction_amount', 'status'];
const RIDEWISE_DISPLAY_COLUMNS = ['id', 'trip_date', 'driver_number', 'vehicle_identifier', 'operator_transaction_ref', 'fare_total', 'concession_type'];

const ReportUpload: React.FC = () => {
  const [eftposFile, setEftposFile] = useState<UploadedFile | null>(null);
  const [totalMobilityFile, setTotalMobilityFile] = useState<UploadedFile | null>(null);
  
  // File input refs for clearing
  const eftposInputRef = useRef<HTMLInputElement>(null);
  const totalMobilityInputRef = useRef<HTMLInputElement>(null);
  
  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState<'eftpos' | 'totalmobility' | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [allRows, setAllRows] = useState<Record<string, string>[]>([]); // Store all rows before filtering
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [duplicateAction, setDuplicateAction] = useState<'skip' | 'overwrite'>('skip');
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  
  // Filter state for EFTPOS
  const [excludeReversals, setExcludeReversals] = useState(true);
  
  // Column scroll state
  const [columnOffset, setColumnOffset] = useState(0);
  const VISIBLE_COLUMNS = 8;
  
  // Upload history state
  const [uploadHistory, setUploadHistory] = useState<UploadHistoryRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Delete records dialog state
  const [showDeleteRecordsDialog, setShowDeleteRecordsDialog] = useState(false);
  const [deleteRecordsUpload, setDeleteRecordsUpload] = useState<UploadHistoryRecord | null>(null);
  const [deleteRecordsData, setDeleteRecordsData] = useState<any[]>([]);
  const [isLoadingDeleteRecords, setIsLoadingDeleteRecords] = useState(false);
  const [selectedDeleteRecordIds, setSelectedDeleteRecordIds] = useState<Set<number>>(new Set());
  const [isDeletingRecords, setIsDeletingRecords] = useState(false);
  const [deleteRecordsPage, setDeleteRecordsPage] = useState(0);
  const DELETE_RECORDS_PAGE_SIZE = 100;

  // Date issues collapsible state
  const [showDateIssues, setShowDateIssues] = useState(false);



  // Load upload history on mount
  useEffect(() => {
    loadUploadHistory();
  }, []);

  const loadUploadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('upload_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading upload history:', error);
      } else {
        setUploadHistory(data || []);
      }
    } catch (err) {
      console.error('Error loading upload history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Log upload to history
  const logUpload = async (
    uploadType: string,
    filename: string,
    recordCount: number,
    duplicatesFound: number,
    duplicatesSkipped: number,
    duplicatesOverwritten: number,
    status: 'success' | 'error' | 'partial',
    errorMessage?: string,
    uploadBatchId?: string
  ) => {
    try {
      await supabase.from('upload_history').insert({
        upload_type: uploadType,
        filename: filename,
        record_count: recordCount,
        duplicates_found: duplicatesFound,
        duplicates_skipped: duplicatesSkipped,
        duplicates_overwritten: duplicatesOverwritten,
        status: status,
        error_message: errorMessage || null,
        uploaded_by: 'Admin',
        upload_batch_id: uploadBatchId || null
      });
      
      // Refresh history
      loadUploadHistory();
    } catch (err) {
      console.error('Error logging upload:', err);
    }
  };

  // Normalize header: remove asterisks, trim, lowercase, replace spaces with underscores
  const normalizeHeader = (header: string, type: 'eftpos' | 'totalmobility'): string => {
    let normalized = header
      .replace(/\*/g, '') // Remove asterisks
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    
    // Apply context-aware mapping
    const mapping = type === 'eftpos' ? EFTPOS_HEADER_MAPPING : RIDEWISE_HEADER_MAPPING;
    if (mapping[normalized]) {
      normalized = mapping[normalized];
    }
    
    return normalized;
  };

  // Parse CSV file
  const parseCSV = (text: string, type: 'eftpos' | 'totalmobility'): { headers: string[], rows: Record<string, string>[] } => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Parse header row - handle potential date format hints in first row
    let headerLine = lines[0];
    let dataStartIndex = 1;
    
    // Check if first row contains date format hints (like "mm/dd/yyyy h:mm:ss AM/PM")
    if (headerLine.includes('mm/dd/yyyy') || headerLine.includes('yyyy-mm-dd')) {
      // Skip the date format hint row
      headerLine = lines[1] || '';
      dataStartIndex = 2;
    }

    // Normalize headers (remove asterisks, apply mapping)
    const rawHeaders = headerLine.split(',').map(h => h.trim());
    const headers = rawHeaders.map(h => normalizeHeader(h, type));
    
    const rows: Record<string, string>[] = [];
    for (let i = dataStartIndex; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim() || '';
        });
        rows.push(row);
      } else if (values.length > 0) {
        // Handle rows with different column count - try to match what we can
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim() || '';
        });
        rows.push(row);
      }
    }

    return { headers, rows };
  };

  // Parse a single CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Convert "mm/dd/yyyy h:mm:ss AM/PM" or "mm/dd/yyyy HH:mm" to ISO timestamp string
  // IMPORTANT: Constructs the timestamp string directly to avoid browser timezone conversion
  const parseEftposDateTime = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    try {
      // Handle format: "mm/dd/yyyy h:mm:ss AM/PM" or "mm/dd/yyyy HH:mm" (with or without seconds, with or without AM/PM)
      const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
      
      if (match) {
        const [, month, day, year, hours, minutes, seconds, ampm] = match;
        let hour = parseInt(hours);
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        const yearNum = parseInt(year);
        const minNum = parseInt(minutes);
        const sec = seconds ? parseInt(seconds) : 0;
        
        // Validate date components to avoid constructing invalid ISO strings
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum < 1900 || yearNum > 2100) {
          return null;
        }
        
        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hour !== 12) {
            hour += 12;
          } else if (ampm.toUpperCase() === 'AM' && hour === 12) {
            hour = 0;
          }
        }
        
        // Validate time components
        if (hour < 0 || hour > 23 || minNum < 0 || minNum > 59 || sec < 0 || sec > 59) {
          return null;
        }
        
        // Construct ISO string directly WITHOUT using new Date() to avoid timezone conversion
        const isoStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minNum).padStart(2, '0')}:${String(sec).padStart(2, '0')}+00:00`;
        return isoStr;
      }
      
      // Fallback: try to parse as standard date but construct string manually
      // Handle ISO-like formats that already have timezone info
      if (dateStr.includes('T') || dateStr.includes('+') || dateStr.endsWith('Z')) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      return null;
    } catch {
      return null;
    }
  };



  // Convert various date formats to "yyyy-mm-dd"
  // EFTPOS settlement_date is yyyy-mm-dd; slash-separated dates are treated as US-style mm/dd/yyyy
  // Dash-separated dates (dd-mm-yyyy) are treated as NZ/AU format (day first)
  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    const trimmed = dateStr.trim();
    
    // Already in yyyy-mm-dd format (EFTPOS settlement_date standard format)
    const yyyymmddMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (yyyymmddMatch) {
      const [, year, month, day] = yyyymmddMatch;
      const m = parseInt(month), d = parseInt(day), y = parseInt(year);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return null; // Invalid date components
    }
    
    // Handle dd-mm-yyyy format with dashes (NZ/AU format - day first)
    const ddmmyyyyDashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (ddmmyyyyDashMatch) {
      const [, day, month, year] = ddmmyyyyDashMatch;
      const m = parseInt(month), d = parseInt(day), y = parseInt(year);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return null;
    }
    
    // Handle mm/dd/yyyy format with slashes (US-style - month first, used by EFTPOS)
    const mmddyyyySlashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyySlashMatch) {
      const [, month, day, year] = mmddyyyySlashMatch;
      const m = parseInt(month), d = parseInt(day), y = parseInt(year);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return null;
    }
    
    // Handle yyyy/mm/dd format
    const yyyymmddSlashMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (yyyymmddSlashMatch) {
      const [, year, month, day] = yyyymmddSlashMatch;
      const m = parseInt(month), d = parseInt(day), y = parseInt(year);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return null;
    }
    
    // Try to parse other formats as fallback
    try {
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        // Extract date parts to avoid timezone issues
        const y = date.getFullYear();
        const m = date.getMonth() + 1;
        const d = date.getDate();
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    } catch {
      // ignore
    }
    
    return null;
  };

  // Validate date fields in rows and return issues
  const validateDateFields = (rows: Record<string, string>[], type: 'eftpos' | 'totalmobility'): DateIssue[] => {
    const issues: DateIssue[] = [];
    
    rows.forEach((row, index) => {
      const rowNum = index + 1; // 1-based row number
      
      if (type === 'eftpos') {
        // Check transaction_date (mm/dd/yyyy h:mm:ss AM/PM format)
        const txnDateRaw = row.transaction_date || '';
        if (txnDateRaw.trim()) {
          const parsed = parseEftposDateTime(txnDateRaw);
          if (!parsed) {
            issues.push({
              row: rowNum,
              field: 'transaction_date',
              rawValue: txnDateRaw,
              reason: 'Could not parse as mm/dd/yyyy h:mm:ss AM/PM format'
            });
          }
        } else {
          issues.push({
            row: rowNum,
            field: 'transaction_date',
            rawValue: '(empty)',
            reason: 'Transaction date is empty'
          });
        }
        
        // Check settlement_date (yyyy-mm-dd format)
        const settleDateRaw = row.settlement_date || '';
        if (settleDateRaw.trim()) {
          const parsed = parseDate(settleDateRaw);
          if (!parsed) {
            issues.push({
              row: rowNum,
              field: 'settlement_date',
              rawValue: settleDateRaw,
              reason: 'Could not parse as yyyy-mm-dd, mm/dd/yyyy, or dd-mm-yyyy format'
            });
          }
        }
        // settlement_date can be empty - not flagged as issue
      } else {
        // RideWise: check trip_date
        const tripDateRaw = row.trip_date || '';
        if (tripDateRaw.trim()) {
          const parsed = parseDate(tripDateRaw);
          if (!parsed) {
            issues.push({
              row: rowNum,
              field: 'trip_date',
              rawValue: tripDateRaw,
              reason: 'Could not parse as dd-mm-yyyy or yyyy-mm-dd format'
            });
          }
        }
      }
    });
    
    return issues;
  };


  // Parse time string (HH:mm:ss or HH:mm)
  const parseTime = (timeStr: string): string | null => {
    if (!timeStr || timeStr.trim() === '') return null;
    
    // Already in correct format
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
      return timeStr;
    }
    
    return timeStr;
  };

  // Validate headers against expected (with flexibility for mapped headers)
  const validateHeaders = (headers: string[], expectedHeaders: string[]): string[] => {
    const errors: string[] = [];
    
    // Make validation more lenient - only require critical fields
    const criticalEftposHeaders = ['txn_number', 'transaction_date'];
    const criticalRidewiseHeaders = ['operator_transaction_ref', 'trip_date'];
    
    const criticalHeaders = expectedHeaders === EFTPOS_HEADERS ? criticalEftposHeaders : criticalRidewiseHeaders;
    
    criticalHeaders.forEach(expected => {
      if (!headers.includes(expected)) {
        errors.push(`Missing required column: ${expected}`);
      }
    });
    
    return errors;
  };

  // Filter EFTPOS rows based on status (exclude reversals)
  const filterEftposRows = (rows: Record<string, string>[], shouldExcludeReversals: boolean): { filtered: Record<string, string>[], excludedCount: number } => {
    if (!shouldExcludeReversals) {
      return { filtered: rows, excludedCount: 0 };
    }
    
    const filtered = rows.filter(row => {
      const status = (row.status || '').toLowerCase().trim();
      const type = (row.type || '').toLowerCase().trim();
      
      // Exclude reversals, refunds, declined, etc.
      const excludeStatuses = ['reversal', 'reversed', 'refund', 'refunded', 'declined', 'cancelled', 'canceled', 'void', 'voided', 'reject', 'rejected'];
      const excludeTypes = ['reversal', 'refund', 'void', 'declined'];
      
      const isExcludedStatus = excludeStatuses.some(s => status.includes(s));
      const isExcludedType = excludeTypes.some(t => type.includes(t));
      
      // Keep the row only if it's NOT excluded
      return !isExcludedStatus && !isExcludedType;
    });
    
    return { filtered, excludedCount: rows.length - filtered.length };
  };

  // Normalize date for comparison (strip timezone, milliseconds, etc.)
  // Extracts YYYY-MM-DDTHH:MM:SS directly from ISO-like strings to avoid new Date() errors
  const normalizeDateForComparison = (dateStr: string | null): string | null => {
    if (!dateStr) return null;
    try {
      // If it looks like an ISO string (contains 'T'), extract directly
      const tIndex = dateStr.indexOf('T');
      if (tIndex !== -1) {
        // Strip timezone info (+00:00, Z, etc.) and take first 19 chars: YYYY-MM-DDTHH:MM:SS
        const stripped = dateStr.replace(/[+Z].*$/, '');
        if (stripped.length >= 19) {
          return stripped.slice(0, 19);
        }
        return stripped;
      }
      // Fallback to Date parsing with safety check
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().slice(0, 19);
    } catch {
      return null;
    }
  };

  // Check for duplicates in EFTPOS data using transaction_date only
  const checkEftposDuplicates = async (rows: Record<string, string>[]): Promise<{ duplicates: Record<string, string>[], newRecords: Record<string, string>[] }> => {
    // Parse all transaction dates from the CSV
    const rowsWithDates = rows.map(r => ({
      row: r,
      transaction_date: parseEftposDateTime(r.transaction_date)
    })).filter(r => r.transaction_date);
    
    if (rowsWithDates.length === 0) {
      return { duplicates: [], newRecords: rows };
    }

    // Get unique dates for querying (just the date part for initial filtering)
    // Extract date part directly from ISO string (YYYY-MM-DD) without using new Date()
    const uniqueDates = [...new Set(rowsWithDates.map(r => {
      const isoStr = r.transaction_date!;
      const tIndex = isoStr.indexOf('T');
      if (tIndex !== -1) {
        return isoStr.substring(0, tIndex); // e.g., "2026-02-08"
      }
      // Fallback: try Date parsing with safety
      try {
        const date = new Date(isoStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch { /* ignore */ }
      return null;
    }).filter(Boolean) as string[])];


    // Query existing records by date range
    const existingDates = new Set<string>();
    
    // Query in batches by date
    const batchSize = 50;
    for (let i = 0; i < uniqueDates.length; i += batchSize) {
      const batchDates = uniqueDates.slice(i, i + batchSize);
      
      // Query for each date in the batch
      for (const dateStr of batchDates) {
        const startOfDay = `${dateStr}T00:00:00.000Z`;
        const endOfDay = `${dateStr}T23:59:59.999Z`;
        
        const { data } = await supabase
          .from('eftpos_data')
          .select('transaction_date')
          .gte('transaction_date', startOfDay)
          .lte('transaction_date', endOfDay);
        
        if (data) {
          data.forEach(d => {
            if (d.transaction_date) {
              // Normalize to seconds precision for comparison
              const normalizedDate = normalizeDateForComparison(d.transaction_date);
              if (normalizedDate) {
                existingDates.add(normalizedDate);
              }
            }
          });
        }
      }
    }

    const duplicates: Record<string, string>[] = [];
    const newRecords: Record<string, string>[] = [];

    rows.forEach(row => {
      const transactionDate = parseEftposDateTime(row.transaction_date);
      const normalizedDate = normalizeDateForComparison(transactionDate);
      
      if (normalizedDate && existingDates.has(normalizedDate)) {
        duplicates.push(row);
      } else {
        newRecords.push(row);
      }
    });

    return { duplicates, newRecords };
  };


  // Check for duplicates in RideWise data using operator_transaction_ref
  const checkRideWiseDuplicates = async (rows: Record<string, string>[]): Promise<{ duplicates: Record<string, string>[], newRecords: Record<string, string>[] }> => {
    // Use operator_transaction_ref for duplicate detection
    const operatorRefs = rows.map(r => r.operator_transaction_ref).filter(Boolean);
    
    if (operatorRefs.length === 0) {
      return { duplicates: [], newRecords: rows };
    }

    // Query existing operator_transaction_refs in batches
    const batchSize = 500;
    const existingRefs = new Set<string>();
    
    for (let i = 0; i < operatorRefs.length; i += batchSize) {
      const batch = operatorRefs.slice(i, i + batchSize);
      const { data } = await supabase
        .from('ridewise_data')
        .select('operator_transaction_ref')
        .in('operator_transaction_ref', batch);
      
      if (data) {
        data.forEach(d => {
          if (d.operator_transaction_ref) {
            existingRefs.add(d.operator_transaction_ref);
          }
        });
      }
    }

    const duplicates: Record<string, string>[] = [];
    const newRecords: Record<string, string>[] = [];

    rows.forEach(row => {
      if (row.operator_transaction_ref && existingRefs.has(row.operator_transaction_ref)) {
        duplicates.push(row);
      } else {
        newRecords.push(row);
      }
    });

    return { duplicates, newRecords };
  };

  // Apply filters and check duplicates - now accepts filter value directly
  const applyFiltersAndCheckDuplicates = async (rows: Record<string, string>[], type: 'eftpos' | 'totalmobility', shouldExcludeReversals?: boolean) => {
    setIsCheckingDuplicates(true);
    
    try {
      let filteredRows = rows;
      let filteredOutCount = 0;
      
      // Apply EFTPOS-specific filters - use passed value or current state
      if (type === 'eftpos') {
        const filterValue = shouldExcludeReversals !== undefined ? shouldExcludeReversals : excludeReversals;
        const { filtered, excludedCount } = filterEftposRows(rows, filterValue);
        filteredRows = filtered;
        filteredOutCount = excludedCount;
      }
      
      // Check for duplicates
      let duplicateResult;
      if (type === 'eftpos') {
        duplicateResult = await checkEftposDuplicates(filteredRows);
      } else {
        duplicateResult = await checkRideWiseDuplicates(filteredRows);
      }
      
      setParsedData(prev => prev ? {
        ...prev,
        rows: filteredRows,
        duplicates: duplicateResult.duplicates,
        newRecords: duplicateResult.newRecords,
        filteredOutCount
      } : null);
    } catch (err) {
      console.error('Error checking duplicates:', err);
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  // Handle file selection and parsing
  const handleFileSelect = async (file: File, type: 'eftpos' | 'totalmobility') => {
    if (!file) return;

    const text = await file.text();
    const { headers, rows } = parseCSV(text, type);
    
    const expectedHeaders = type === 'eftpos' ? EFTPOS_HEADERS : RIDEWISE_HEADERS;
    const errors = validateHeaders(headers, expectedHeaders);
    
    // Store all rows for filtering
    setAllRows(rows);
    // Validate date fields and track issues
    const dateIssues = validateDateFields(rows, type);
    if (dateIssues.length > 0) {
      console.warn(`[${type.toUpperCase()} Upload] ${dateIssues.length} date parsing issues found in ${file.name}`);
      dateIssues.slice(0, 10).forEach(issue => {
        console.warn(`  Row ${issue.row}: ${issue.field} = "${issue.rawValue}" — ${issue.reason}`);
      });
      if (dateIssues.length > 10) {
        console.warn(`  ... and ${dateIssues.length - 10} more issues`);
      }
    }
    
    // Set initial parsed data without duplicate check
    setParsedData({ headers, rows, errors, duplicates: [], newRecords: rows, filteredOutCount: 0, dateIssues });
    setPreviewType(type);
    setShowPreview(true);
    setDuplicateAction('skip');
    setShowDateIssues(false);

    // Reset filter to default (true for EFTPOS)
    const defaultExcludeReversals = true;
    setExcludeReversals(defaultExcludeReversals);

    if (type === 'eftpos') {
      setEftposFile({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date()
      });
    } else {
      setTotalMobilityFile({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date()
      });
    }

    // Check for duplicates (with filters for EFTPOS) - pass the filter value directly
    if (errors.length === 0 && rows.length > 0) {
      await applyFiltersAndCheckDuplicates(rows, type, type === 'eftpos' ? defaultExcludeReversals : undefined);
    }
  };

  // Handle filter change
  const handleFilterChange = async (newExcludeReversals: boolean) => {
    setExcludeReversals(newExcludeReversals);
    if (previewType === 'eftpos' && allRows.length > 0) {
      // Pass the new value directly to avoid state timing issues
      await applyFiltersAndCheckDuplicates(allRows, 'eftpos', newExcludeReversals);
    }
  };

  // Clear selected file
  const clearFile = (type: 'eftpos' | 'totalmobility') => {
    if (type === 'eftpos') {
      setEftposFile(null);
      if (eftposInputRef.current) {
        eftposInputRef.current.value = '';
      }
    } else {
      setTotalMobilityFile(null);
      if (totalMobilityInputRef.current) {
        totalMobilityInputRef.current.value = '';
      }
    }
  };

  // Upload EFTPOS data to database
  const uploadEftposData = async (rows: Record<string, string>[], overwrite: boolean = false, batchId?: string) => {
    const batchSize = 100;
    let successCount = 0;
    let errorCount = 0;
    const failedRows: { rowIndex: number; reason: string }[] = [];

    // Log first few settlement_date values for debugging
    console.log(`[EFTPOS Upload] Starting upload of ${rows.length} records (batchId: ${batchId})`);
    rows.slice(0, 5).forEach((row, i) => {
      const rawSettlement = row.settlement_date || '(empty)';
      const parsedSettlement = parseDate(row.settlement_date);
      const rawTxnDate = row.transaction_date || '(empty)';
      const parsedTxnDate = parseEftposDateTime(row.transaction_date);
      console.log(`  Row ${i + 1}: settlement_date raw="${rawSettlement}" → parsed="${parsedSettlement}" | transaction_date raw="${rawTxnDate}" → parsed="${parsedTxnDate}"`);
    });
    if (rows.length > 5) {
      console.log(`  ... (${rows.length - 5} more rows)`);
    }

    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      
      const formattedBatch = batch.map((row, batchIdx) => {
        const parsedTxnDate = parseEftposDateTime(row.transaction_date);
        const parsedSettleDate = parseDate(row.settlement_date);
        
        // Log any rows where date parsing failed
        if (row.transaction_date && !parsedTxnDate) {
          const globalIdx = i + batchIdx + 1;
          console.warn(`  [EFTPOS Upload] Row ${globalIdx}: transaction_date parse FAILED — raw="${row.transaction_date}"`);
          failedRows.push({ rowIndex: globalIdx, reason: `transaction_date parse failed: "${row.transaction_date}"` });
        }
        if (row.settlement_date && !parsedSettleDate) {
          const globalIdx = i + batchIdx + 1;
          console.warn(`  [EFTPOS Upload] Row ${globalIdx}: settlement_date parse FAILED — raw="${row.settlement_date}"`);
          failedRows.push({ rowIndex: globalIdx, reason: `settlement_date parse failed: "${row.settlement_date}"` });
        }
        
        return {
          merchant_id: row.merchant_id || null,
          terminal_id: row.terminal_id || null,
          transaction_date: parsedTxnDate,
          settlement_date: parsedSettleDate,
          pan: row.pan || null,
          txn_number: row.txn_number || null,
          logo: row.logo || null,
          type: row.type || null,
          transaction_amount: row.transaction_amount ? parseFloat(row.transaction_amount) : null,
          purchase: row.purchase ? parseFloat(row.purchase) : null,
          cash: row.cash ? parseFloat(row.cash) : null,
          status: row.status || null,
          upload_date: new Date().toISOString(),
          upload_batch_id: batchId || null
        };
      });

      if (overwrite) {
        // Delete existing records first, then insert
        for (const record of formattedBatch) {
          if (record.txn_number && record.transaction_date) {
            await supabase.from('eftpos_data')
              .delete()
              .eq('txn_number', record.txn_number)
              .eq('transaction_date', record.transaction_date);
          }
        }
      }

      const { error } = await supabase
        .from('eftpos_data')
        .insert(formattedBatch);

      if (error) {
        console.error('EFTPOS upload error:', error);
        errorCount += batch.length;
      } else {
        successCount += batch.length;
      }

      setUploadProgress({ current: Math.min(i + batchSize, rows.length), total: rows.length });
    }

    // Log summary of failed rows
    if (failedRows.length > 0) {
      console.warn(`[EFTPOS Upload] ${failedRows.length} rows had date parsing issues during upload:`);
      failedRows.slice(0, 20).forEach(f => console.warn(`  Row ${f.rowIndex}: ${f.reason}`));
      if (failedRows.length > 20) console.warn(`  ... and ${failedRows.length - 20} more`);
    }
    console.log(`[EFTPOS Upload] Complete: ${successCount} success, ${errorCount} errors`);

    return { successCount, errorCount };
  };


  // Upload RideWise/Total Mobility data to database
  const uploadRideWiseData = async (rows: Record<string, string>[], overwrite: boolean = false, batchId?: string) => {
    const batchSize = 100;
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      
      const formattedBatch = batch.map(row => ({
        driver_number: row.driver_number || null,
        vehicle_identifier: row.vehicle_identifier || null,
        transaction_type: row.transaction_type || null,
        region: row.region || null,
        voucher_ref_number: row.voucher_ref_number || null,
        operator_transaction_ref: row.operator_transaction_ref || null,
        concession_type: row.concession_type || null,
        trip_date: parseDate(row.trip_date),
        pickup_location: row.pickup_location || null,
        dropoff_location: row.dropoff_location || null,
        pickup_gis: row.pickup_gis || null,
        dropoff_gis: row.dropoff_gis || null,
        start_time: parseTime(row.start_time),
        end_time: parseTime(row.end_time),
        fare_paid: row.fare_paid ? parseFloat(row.fare_paid) : null,
        concession_amount: row.concession_amount ? parseFloat(row.concession_amount) : null,
        fare_total: row.fare_total ? parseFloat(row.fare_total) : null,
        number_of_passengers: row.number_of_passengers ? parseInt(row.number_of_passengers) : null,
        kilometres: row.kilometres ? parseFloat(row.kilometres) : null,
        hoist_deployed: row.hoist_deployed?.toLowerCase() === 'true' || row.hoist_deployed === '1' || row.hoist_deployed?.toLowerCase() === 'yes',
        client_number: row.client_number || null,
        client_surname: row.client_surname || null,
        client_first_name: row.client_first_name || null,
        additional_information: row.additional_information || null,
        upload_date: new Date().toISOString(),
        upload_batch_id: batchId || null
      }));

      if (overwrite) {
        // Delete existing records first, then insert
        for (const record of formattedBatch) {
          if (record.operator_transaction_ref) {
            await supabase.from('ridewise_data')
              .delete()
              .eq('operator_transaction_ref', record.operator_transaction_ref);
          }
        }
      }

      const { error } = await supabase
        .from('ridewise_data')
        .insert(formattedBatch);

      if (error) {
        console.error('RideWise upload error:', error);
        errorCount += batch.length;
      } else {
        successCount += batch.length;
      }

      setUploadProgress({ current: Math.min(i + batchSize, rows.length), total: rows.length });
    }

    return { successCount, errorCount };
  };

  // Handle upload confirmation
  const handleUploadConfirm = async () => {
    if (!parsedData || !previewType) return;

    const filename = previewType === 'eftpos' ? eftposFile?.name : totalMobilityFile?.name;
    const recordsToUpload = duplicateAction === 'skip' ? parsedData.newRecords : parsedData.rows;
    const duplicatesFound = parsedData.duplicates.length;
    const duplicatesSkipped = duplicateAction === 'skip' ? duplicatesFound : 0;
    const duplicatesOverwritten = duplicateAction === 'overwrite' ? duplicatesFound : 0;

    // Generate a unique batch ID for this upload
    const batchId = crypto.randomUUID();

    setIsUploading(true);
    setUploadProgress({ current: 0, total: recordsToUpload.length });

    try {
      let result;
      if (previewType === 'eftpos') {
        result = await uploadEftposData(recordsToUpload, duplicateAction === 'overwrite', batchId);
      } else {
        result = await uploadRideWiseData(recordsToUpload, duplicateAction === 'overwrite', batchId);
      }

      const status = result.errorCount === 0 ? 'success' : (result.successCount > 0 ? 'partial' : 'error');
      
      // Log the upload with batch ID
      await logUpload(
        previewType === 'eftpos' ? 'EFTPOS' : 'Total Mobility',
        filename || 'unknown',
        result.successCount,
        duplicatesFound,
        duplicatesSkipped,
        duplicatesOverwritten,
        status,
        result.errorCount > 0 ? `${result.errorCount} records failed to upload` : undefined,
        batchId
      );

      if (result.errorCount === 0) {
        toast({
          title: 'Upload Successful',
          description: `${result.successCount} records uploaded to ${previewType === 'eftpos' ? 'EFTPOS' : 'Total Mobility'} database.${duplicatesSkipped > 0 ? ` ${duplicatesSkipped} duplicates skipped.` : ''}${duplicatesOverwritten > 0 ? ` ${duplicatesOverwritten} duplicates overwritten.` : ''}`,
        });
      } else {
        toast({
          title: 'Upload Completed with Errors',
          description: `${result.successCount} records uploaded, ${result.errorCount} failed.`,
          variant: 'destructive'
        });
      }

      setShowPreview(false);
      setParsedData(null);
      setPreviewType(null);
      setAllRows([]);
      clearFile('eftpos');
      clearFile('totalmobility');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Log the failed upload
      await logUpload(
        previewType === 'eftpos' ? 'EFTPOS' : 'Total Mobility',
        filename || 'unknown',
        0,
        duplicatesFound,
        0,
        0,
        'error',
        'An error occurred while uploading the data',
        batchId
      );

      toast({
        title: 'Upload Failed',
        description: 'An error occurred while uploading the data.',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // EFTPOS upload card with database integration
  const EftposUploadCard = () => (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader>
        <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          EFTPOS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="eftpos-upload" className="text-yellow-500">
            Upload EFTPOS Report
          </Label>
          <Input
            ref={eftposInputRef}
            id="eftpos-upload"
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file, 'eftpos');
            }}
            className="mt-2 dark-input"
          />
        </div>
        
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium">Expected CSV format:</p>
          <p className="text-yellow-600">Date format: transaction_date = mm/dd/yyyy h:mm:ss AM/PM</p>
          <p className="text-yellow-600">Date format: settlement_date = yyyy-mm-dd</p>
          <p className="text-purple-400 mt-2">Duplicate check: transaction_date</p>
        </div>
        

        {eftposFile && (
          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">File Selected</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearFile('eftpos')}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="mt-2 text-sm text-green-300">
              <p><strong>Name:</strong> {eftposFile.name}</p>
              <p><strong>Size:</strong> {formatFileSize(eftposFile.size)}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Total Mobility upload card with database integration
  const TotalMobilityUploadCard = () => (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader>
        <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Total Mobility
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="totalmobility-upload" className="text-yellow-500">
            Upload Total Mobility Report
          </Label>
          <Input
            ref={totalMobilityInputRef}
            id="totalmobility-upload"
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file, 'totalmobility');
            }}
            className="mt-2 dark-input"
          />
        </div>
        
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium">Expected CSV format:</p>
          <p className="text-yellow-600">Date format: trip_date = dd-mm-yyyy or yyyy-mm-dd</p>
          <p className="text-purple-400 mt-2">Duplicate check: operator_transaction_ref</p>
        </div>
        
        {totalMobilityFile && (
          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">File Selected</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearFile('totalmobility')}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="mt-2 text-sm text-green-300">
              <p><strong>Name:</strong> {totalMobilityFile.name}</p>
              <p><strong>Size:</strong> {formatFileSize(totalMobilityFile.size)}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // ===== DELETE RECORDS FUNCTIONALITY =====

  // Open the delete records dialog for a specific upload
  const handleOpenDeleteRecords = async (record: UploadHistoryRecord) => {
    setDeleteRecordsUpload(record);
    setShowDeleteRecordsDialog(true);
    setSelectedDeleteRecordIds(new Set());
    setDeleteRecordsPage(0);
    setIsLoadingDeleteRecords(true);
    setDeleteRecordsData([]);

    try {
      const tableName = record.upload_type === 'EFTPOS' ? 'eftpos_data' : 'ridewise_data';
      
      if (record.upload_batch_id) {
        // Query by batch ID - fetch all records
        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .eq('upload_batch_id', record.upload_batch_id)
          .order('id', { ascending: true });

        if (error) {
          console.error('Error loading records:', error);
          toast({
            title: 'Error Loading Records',
            description: error.message,
            variant: 'destructive'
          });
          setDeleteRecordsData([]);
        } else {
          setDeleteRecordsData(data || []);
        }
      } else {
        // No batch ID - show message that records can't be linked
        toast({
          title: 'No Batch ID',
          description: 'This upload was made before batch tracking was enabled. Records cannot be identified for deletion.',
          variant: 'destructive'
        });
        setDeleteRecordsData([]);
      }
    } catch (err) {
      console.error('Error loading records:', err);
      toast({
        title: 'Error',
        description: 'Failed to load records for this upload.',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingDeleteRecords(false);
    }
  };

  // Toggle selection of a delete record
  const toggleDeleteRecordSelection = (id: number) => {
    setSelectedDeleteRecordIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Toggle select all delete records
  const toggleSelectAllDeleteRecords = () => {
    if (selectedDeleteRecordIds.size === deleteRecordsData.length) {
      setSelectedDeleteRecordIds(new Set());
    } else {
      setSelectedDeleteRecordIds(new Set(deleteRecordsData.map((r: any) => r.id)));
    }
  };

  // Delete selected records from the data table
  const handleDeleteSelectedRecords = async () => {
    if (selectedDeleteRecordIds.size === 0 || !deleteRecordsUpload) return;

    setIsDeletingRecords(true);
    try {
      const tableName = deleteRecordsUpload.upload_type === 'EFTPOS' ? 'eftpos_data' : 'ridewise_data';
      const idsToDelete = Array.from(selectedDeleteRecordIds);
      const totalToDelete = idsToDelete.length;
      let deletedCount = 0;

      // Delete in batches
      const batchSize = 100;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        const { error } = await supabase
          .from(tableName)
          .delete()
          .in('id', batch);

        if (error) {
          console.error('Error deleting records:', error);
          toast({
            title: 'Delete Error',
            description: `Error deleting records: ${error.message}`,
            variant: 'destructive'
          });
          break;
        }
        deletedCount += batch.length;
      }

      if (deletedCount === totalToDelete) {
        toast({
          title: 'Records Deleted',
          description: `${deletedCount} record${deletedCount !== 1 ? 's' : ''} deleted from ${deleteRecordsUpload.upload_type} data.`,
        });
      }

      // If all records from this upload were deleted, also delete the upload history entry
      if (deletedCount === deleteRecordsData.length) {
        await supabase
          .from('upload_history')
          .delete()
          .eq('id', deleteRecordsUpload.id);
        
        setShowDeleteRecordsDialog(false);
        setDeleteRecordsUpload(null);
        setDeleteRecordsData([]);
        loadUploadHistory();
      } else {
        // Refresh the records list to show remaining
        const remaining = deleteRecordsData.filter((r: any) => !selectedDeleteRecordIds.has(r.id));
        setDeleteRecordsData(remaining);
        setSelectedDeleteRecordIds(new Set());
        
        // Update the upload history record count
        if (deleteRecordsUpload.upload_batch_id) {
          await supabase
            .from('upload_history')
            .update({ record_count: remaining.length })
            .eq('id', deleteRecordsUpload.id);
        }
        loadUploadHistory();
      }
    } catch (err) {
      console.error('Error deleting records:', err);
      toast({
        title: 'Delete Failed',
        description: 'An error occurred while deleting records.',
        variant: 'destructive'
      });
    } finally {
      setIsDeletingRecords(false);
    }
  };

  // Format cell value for display in delete records table
  const formatCellValue = (value: any, column: string): string => {
    if (value === null || value === undefined) return '-';
    if ((column === 'transaction_date' || column === 'trip_date') && typeof value === 'string') {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = String(d.getFullYear()).slice(-2);
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, '0');
          const seconds = String(d.getSeconds()).padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12;
          if (hours === 0) hours = 12;
          return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
        }
        return String(value);
      } catch {
        return String(value);
      }
    }
    if ((column === 'settlement_date') && typeof value === 'string') {
      // settlement_date is stored as yyyy-mm-dd, display as dd-mm-yy
      try {
        const d = new Date(value + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = String(d.getFullYear()).slice(-2);
          return `${day}-${month}-${year}`;
        }
        return String(value);
      } catch {
        return String(value);
      }
    }
    if (column === 'transaction_amount' || column === 'fare_total' || column === 'fare_paid' || column === 'purchase' || column === 'cash' || column === 'concession_amount') {
      return typeof value === 'number' ? `$${value.toFixed(2)}` : String(value);
    }
    return String(value);
  };



  // Upload History Table
  const UploadHistoryTable = () => (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
          <History className="w-5 h-5" />
          Upload History
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadUploadHistory}
            disabled={isLoadingHistory}
            className="border-gray-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingHistory ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
          </div>
        ) : uploadHistory.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No upload history found
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-yellow-500">Date/Time</TableHead>
                    <TableHead className="text-yellow-500">Type</TableHead>
                    <TableHead className="text-yellow-500">Filename</TableHead>
                    <TableHead className="text-yellow-500 text-right">Records</TableHead>
                    <TableHead className="text-yellow-500 text-right">Duplicates</TableHead>
                    <TableHead className="text-yellow-500">Action</TableHead>
                    <TableHead className="text-yellow-500">Status</TableHead>
                    <TableHead className="text-yellow-500">Uploaded By</TableHead>
                    <TableHead className="text-yellow-500 text-center w-[80px]">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploadHistory.map((record) => (
                    <TableRow key={record.id} className="hover:bg-muted/30">
                      <TableCell className="text-foreground whitespace-nowrap">
                        {formatDateTime(record.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          record.upload_type === 'EFTPOS' 
                            ? 'text-blue-400 border-blue-600' 
                            : record.upload_type === 'Total Mobility'
                            ? 'text-green-400 border-green-600'
                            : 'text-purple-400 border-purple-600'
                        }>
                          {record.upload_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground max-w-[200px] truncate" title={record.filename}>
                        {record.filename}
                      </TableCell>
                      <TableCell className="text-foreground text-right">
                        {record.record_count}
                      </TableCell>
                      <TableCell className="text-foreground text-right">
                        {record.duplicates_found > 0 ? (
                          <span className="text-orange-400">{record.duplicates_found}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {record.duplicates_skipped > 0 && (
                          <Badge variant="outline" className="text-yellow-400 border-yellow-600 mr-1">
                            {record.duplicates_skipped} skipped
                          </Badge>
                        )}
                        {record.duplicates_overwritten > 0 && (
                          <Badge variant="outline" className="text-orange-400 border-orange-600">
                            {record.duplicates_overwritten} overwritten
                          </Badge>
                        )}
                        {record.duplicates_skipped === 0 && record.duplicates_overwritten === 0 && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          record.status === 'success' 
                            ? 'bg-green-600 hover:bg-green-700' 
                            : record.status === 'partial'
                            ? 'bg-yellow-600 hover:bg-yellow-700'
                            : 'bg-red-600 hover:bg-red-700'
                        }>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">
                        {record.uploaded_by || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDeleteRecords(record)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0"
                          title="View & delete uploaded records"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );


  // Get duplicate key for a row based on type
  const getDuplicateKey = (row: Record<string, string>, type: 'eftpos' | 'totalmobility'): string => {
    if (type === 'eftpos') {
      const txnNumber = row.txn_number;
      const transactionDate = parseEftposDateTime(row.transaction_date);
      return `${txnNumber}_${transactionDate}`;
    } else {
      return row.operator_transaction_ref || '';
    }
  };

  // Get paginated delete records
  const paginatedDeleteRecords = deleteRecordsData.slice(
    deleteRecordsPage * DELETE_RECORDS_PAGE_SIZE,
    (deleteRecordsPage + 1) * DELETE_RECORDS_PAGE_SIZE
  );
  const totalDeletePages = Math.ceil(deleteRecordsData.length / DELETE_RECORDS_PAGE_SIZE);

  // Get display columns based on upload type
  const deleteDisplayColumns = deleteRecordsUpload?.upload_type === 'EFTPOS' 
    ? EFTPOS_DISPLAY_COLUMNS 
    : RIDEWISE_DISPLAY_COLUMNS;

  return (
    <div className="space-y-6">
      {/* Upload Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EftposUploadCard />
        <TotalMobilityUploadCard />
      </div>

      {/* Upload History */}
      <UploadHistoryTable />

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-xl golden-text flex items-center gap-2">
              <Database className="w-5 h-5" />
              {previewType === 'eftpos' ? 'EFTPOS' : 'Total Mobility'} Data Preview
            </DialogTitle>
          </DialogHeader>

          {parsedData && (
            <div className="space-y-4">
              {/* Validation Status */}
              {parsedData.errors.length > 0 ? (
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Header Validation Errors</span>
                  </div>
                  <ul className="text-sm text-red-300 list-disc list-inside">
                    {parsedData.errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Headers validated successfully</span>
                  </div>
                </div>
              )}

              {/* EFTPOS Filter Options */}
              {previewType === 'eftpos' && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-blue-400">
                      <Filter className="w-5 h-5" />
                      <span className="font-medium">Filter Options</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="exclude-reversals"
                        checked={excludeReversals}
                        onCheckedChange={(checked) => handleFilterChange(checked as boolean)}
                        disabled={isCheckingDuplicates}
                      />
                      <label
                        htmlFor="exclude-reversals"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-blue-300 cursor-pointer"
                      >
                        Exclude Reversals / Refunds / Declined (Approved Only)
                      </label>
                    </div>
                    {parsedData.filteredOutCount > 0 && (
                      <Badge variant="outline" className="text-red-400 border-red-600">
                        {parsedData.filteredOutCount} excluded
                      </Badge>
                    )}
                  </div>
                </div>
              )}


              {/* Date Parsing Issues - Collapsible Section */}
              {parsedData.dateIssues && parsedData.dateIssues.length > 0 && (
                <div className="bg-amber-900/30 border border-amber-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-amber-900/20 transition-colors"
                    onClick={() => setShowDateIssues(!showDateIssues)}
                  >
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">
                        {parsedData.dateIssues.length} Date Parsing Issue{parsedData.dateIssues.length !== 1 ? 's' : ''} Found
                      </span>
                      <Badge variant="outline" className="text-amber-400 border-amber-600 text-xs">
                        Review before uploading
                      </Badge>
                    </div>
                    {showDateIssues ? (
                      <ChevronUp className="w-5 h-5 text-amber-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-amber-400" />
                    )}
                  </button>
                  
                  {showDateIssues && (
                    <div className="border-t border-amber-700 p-4 space-y-3">
                      <p className="text-sm text-amber-300">
                        The following rows have date values that could not be parsed. These rows will still be uploaded, but the date fields will be stored as NULL. 
                        Consider fixing these in your CSV file before uploading.
                      </p>
                      
                      {/* Group issues by field */}
                      {(() => {
                        const txnDateIssues = parsedData.dateIssues.filter(i => i.field === 'transaction_date');
                        const settleDateIssues = parsedData.dateIssues.filter(i => i.field === 'settlement_date');
                        const tripDateIssues = parsedData.dateIssues.filter(i => i.field === 'trip_date');
                        
                        return (
                          <div className="space-y-3">
                            {txnDateIssues.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-red-400 border-red-600 text-xs">
                                    transaction_date
                                  </Badge>
                                  <span className="text-xs text-amber-300">
                                    {txnDateIssues.length} issue{txnDateIssues.length !== 1 ? 's' : ''} — Expected: mm/dd/yyyy h:mm:ss AM/PM
                                  </span>
                                </div>
                                <div className="border border-amber-800 rounded-lg overflow-hidden">
                                  <ScrollArea className="max-h-[150px]">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-amber-900/30">
                                          <TableHead className="text-amber-400 w-[60px]">Row</TableHead>
                                          <TableHead className="text-amber-400">Raw Value</TableHead>
                                          <TableHead className="text-amber-400">Issue</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {txnDateIssues.slice(0, 50).map((issue, idx) => (
                                          <TableRow key={idx} className="hover:bg-amber-900/20">
                                            <TableCell className="text-foreground font-mono">{issue.row}</TableCell>
                                            <TableCell className="text-red-300 font-mono text-xs">{issue.rawValue}</TableCell>
                                            <TableCell className="text-amber-300 text-xs">{issue.reason}</TableCell>
                                          </TableRow>
                                        ))}
                                        {txnDateIssues.length > 50 && (
                                          <TableRow>
                                            <TableCell colSpan={3} className="text-center text-amber-400 text-xs">
                                              ... and {txnDateIssues.length - 50} more issues
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </ScrollArea>
                                </div>
                              </div>
                            )}
                            
                            {settleDateIssues.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-orange-400 border-orange-600 text-xs">
                                    settlement_date
                                  </Badge>
                                  <span className="text-xs text-amber-300">
                                    {settleDateIssues.length} issue{settleDateIssues.length !== 1 ? 's' : ''} — Expected: yyyy-mm-dd
                                  </span>
                                </div>
                                <div className="border border-amber-800 rounded-lg overflow-hidden">
                                  <ScrollArea className="max-h-[150px]">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-amber-900/30">
                                          <TableHead className="text-amber-400 w-[60px]">Row</TableHead>
                                          <TableHead className="text-amber-400">Raw Value</TableHead>
                                          <TableHead className="text-amber-400">Issue</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {settleDateIssues.slice(0, 50).map((issue, idx) => (
                                          <TableRow key={idx} className="hover:bg-amber-900/20">
                                            <TableCell className="text-foreground font-mono">{issue.row}</TableCell>
                                            <TableCell className="text-red-300 font-mono text-xs">{issue.rawValue}</TableCell>
                                            <TableCell className="text-amber-300 text-xs">{issue.reason}</TableCell>
                                          </TableRow>
                                        ))}
                                        {settleDateIssues.length > 50 && (
                                          <TableRow>
                                            <TableCell colSpan={3} className="text-center text-amber-400 text-xs">
                                              ... and {settleDateIssues.length - 50} more issues
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </ScrollArea>
                                </div>
                              </div>
                            )}
                            
                            {tripDateIssues.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-purple-400 border-purple-600 text-xs">
                                    trip_date
                                  </Badge>
                                  <span className="text-xs text-amber-300">
                                    {tripDateIssues.length} issue{tripDateIssues.length !== 1 ? 's' : ''} — Expected: dd-mm-yyyy or yyyy-mm-dd
                                  </span>
                                </div>
                                <div className="border border-amber-800 rounded-lg overflow-hidden">
                                  <ScrollArea className="max-h-[150px]">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-amber-900/30">
                                          <TableHead className="text-amber-400 w-[60px]">Row</TableHead>
                                          <TableHead className="text-amber-400">Raw Value</TableHead>
                                          <TableHead className="text-amber-400">Issue</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {tripDateIssues.slice(0, 50).map((issue, idx) => (
                                          <TableRow key={idx} className="hover:bg-amber-900/20">
                                            <TableCell className="text-foreground font-mono">{issue.row}</TableCell>
                                            <TableCell className="text-red-300 font-mono text-xs">{issue.rawValue}</TableCell>
                                            <TableCell className="text-amber-300 text-xs">{issue.reason}</TableCell>
                                          </TableRow>
                                        ))}
                                        {tripDateIssues.length > 50 && (
                                          <TableRow>
                                            <TableCell colSpan={3} className="text-center text-amber-400 text-xs">
                                              ... and {tripDateIssues.length - 50} more issues
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </ScrollArea>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}

              <div className="flex flex-wrap gap-4">
                <Badge variant="outline" className="text-yellow-400 border-yellow-600">
                  {parsedData.rows.length} total rows
                </Badge>
                <Badge variant="outline" className="text-purple-400 border-purple-600">
                  {parsedData.headers.length} columns
                </Badge>
                {isCheckingDuplicates ? (
                  <Badge variant="outline" className="text-blue-400 border-blue-600">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Checking duplicates...
                  </Badge>
                ) : (
                  <>
                    <Badge variant="outline" className="text-green-400 border-green-600">
                      {parsedData.newRecords.length} new records
                    </Badge>
                    {parsedData.duplicates.length > 0 && (
                      <Badge variant="outline" className="text-orange-400 border-orange-600">
                        {parsedData.duplicates.length} duplicates found
                      </Badge>
                    )}
                  </>
                )}
              </div>

              {/* Duplicate Action Selection */}
              {parsedData.duplicates.length > 0 && !isCheckingDuplicates && (
                <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-400 mb-3">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">
                      {parsedData.duplicates.length} duplicate records found
                    </span>
                  </div>
                  <p className="text-sm text-orange-300 mb-3">
                    These records already exist in the database (matched by {previewType === 'eftpos' ? 'transaction_date' : 'operator_transaction_ref'}).
                    Choose how to handle them:

                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant={duplicateAction === 'skip' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDuplicateAction('skip')}
                      className={duplicateAction === 'skip' ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-gray-600'}
                    >
                      <SkipForward className="w-4 h-4 mr-2" />
                      Skip Duplicates ({parsedData.newRecords.length} will upload)
                    </Button>
                    <Button
                      variant={duplicateAction === 'overwrite' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDuplicateAction('overwrite')}
                      className={duplicateAction === 'overwrite' ? 'bg-orange-600 hover:bg-orange-700' : 'border-gray-600'}
                    >
                      <Replace className="w-4 h-4 mr-2" />
                      Overwrite All ({parsedData.rows.length} will upload)
                    </Button>
                  </div>
                </div>
              )}

              {/* Column Navigation */}
              {parsedData.headers.length > VISIBLE_COLUMNS && (
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setColumnOffset(Math.max(0, columnOffset - 1))}
                    disabled={columnOffset === 0}
                    className="border-gray-600"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Showing columns {columnOffset + 1} - {Math.min(columnOffset + VISIBLE_COLUMNS, parsedData.headers.length)} of {parsedData.headers.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setColumnOffset(Math.min(parsedData.headers.length - VISIBLE_COLUMNS, columnOffset + 1))}
                    disabled={columnOffset >= parsedData.headers.length - VISIBLE_COLUMNS}
                    className="border-gray-600"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}

              {/* Data Preview Tabs */}
              <Tabs defaultValue="all" className="w-full">
                <TabsList className="bg-muted/50">
                  <TabsTrigger value="all">All Data ({parsedData.rows.length})</TabsTrigger>
                  {parsedData.duplicates.length > 0 && (
                    <TabsTrigger value="duplicates" className="text-orange-400">
                      Duplicates ({parsedData.duplicates.length})
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="new" className="text-green-400">
                    New Records ({parsedData.newRecords.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <DataPreviewTable 
                    rows={parsedData.rows} 
                    headers={parsedData.headers} 
                    duplicateKeys={new Set(parsedData.duplicates.map(d => getDuplicateKey(d, previewType!)))}
                    previewType={previewType!}
                    columnOffset={columnOffset}
                    visibleColumns={VISIBLE_COLUMNS}
                  />
                </TabsContent>

                {parsedData.duplicates.length > 0 && (
                  <TabsContent value="duplicates">
                    <DataPreviewTable 
                      rows={parsedData.duplicates} 
                      headers={parsedData.headers} 
                      duplicateKeys={new Set()} 
                      previewType={previewType!}
                      columnOffset={columnOffset}
                      visibleColumns={VISIBLE_COLUMNS}
                    />
                  </TabsContent>
                )}

                <TabsContent value="new">
                  <DataPreviewTable 
                    rows={parsedData.newRecords} 
                    headers={parsedData.headers} 
                    duplicateKeys={new Set()} 
                    previewType={previewType!}
                    columnOffset={columnOffset}
                    visibleColumns={VISIBLE_COLUMNS}
                  />
                </TabsContent>
              </Tabs>

              {/* Upload Progress */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Uploading... {uploadProgress.current} / {uploadProgress.total}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPreview(false);
                setParsedData(null);
                setPreviewType(null);
                setAllRows([]);
                setColumnOffset(0);
              }}
              disabled={isUploading}
              className="border-gray-600"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleUploadConfirm}
              disabled={isUploading || isCheckingDuplicates || (parsedData?.errors.length ?? 0) > 0 || (parsedData?.rows.length ?? 0) === 0}
              className="golden-button"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {duplicateAction === 'skip' ? parsedData?.newRecords.length : parsedData?.rows.length || 0} Records
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Records Dialog */}
      <Dialog open={showDeleteRecordsDialog} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteRecordsDialog(false);
          setDeleteRecordsUpload(null);
          setDeleteRecordsData([]);
          setSelectedDeleteRecordIds(new Set());
          setDeleteRecordsPage(0);
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Records — {deleteRecordsUpload?.upload_type}
            </DialogTitle>
          </DialogHeader>

          {deleteRecordsUpload && (
            <div className="space-y-4">
              {/* Upload info */}
              <div className="bg-muted/30 border border-border rounded-lg p-3">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">File: </span>
                    <span className="text-foreground font-medium">{deleteRecordsUpload.filename}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Uploaded: </span>
                    <span className="text-foreground">{formatDateTime(deleteRecordsUpload.created_at)}</span>
                  </div>
                  <Badge variant="outline" className={
                    deleteRecordsUpload.upload_type === 'EFTPOS' 
                      ? 'text-blue-400 border-blue-600' 
                      : 'text-green-400 border-green-600'
                  }>
                    {deleteRecordsUpload.upload_type}
                  </Badge>
                </div>
              </div>

              {isLoadingDeleteRecords ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                  <span className="ml-3 text-muted-foreground">Loading records...</span>
                </div>
              ) : deleteRecordsData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-3 text-yellow-500" />
                  <p className="text-lg font-medium">No records found</p>
                  <p className="text-sm mt-1">
                    {deleteRecordsUpload.upload_batch_id 
                      ? 'No records found for this upload batch.' 
                      : 'This upload was made before batch tracking was enabled. Records cannot be identified for deletion.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Select all bar */}
                  <div className="flex items-center justify-between bg-muted/20 border border-border rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="select-all-delete-records"
                        checked={deleteRecordsData.length > 0 && selectedDeleteRecordIds.size === deleteRecordsData.length}
                        onCheckedChange={toggleSelectAllDeleteRecords}
                      />
                      <label htmlFor="select-all-delete-records" className="text-sm font-medium text-foreground cursor-pointer">
                        Select All ({deleteRecordsData.length} records)
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedDeleteRecordIds.size > 0 && (
                        <Badge variant="outline" className="text-red-400 border-red-600">
                          {selectedDeleteRecordIds.size} selected
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-yellow-400 border-yellow-600">
                        {deleteRecordsData.length} total records
                      </Badge>
                    </div>
                  </div>

                  {/* Records table */}
                  <div className="border border-border rounded-lg overflow-hidden">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-[40px]"></TableHead>
                            {deleteDisplayColumns.map((col) => (
                              <TableHead key={col} className="text-yellow-500 font-medium whitespace-nowrap">
                                {col}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedDeleteRecords.map((record: any) => (
                            <TableRow 
                              key={record.id} 
                              className={`hover:bg-muted/30 cursor-pointer ${
                                selectedDeleteRecordIds.has(record.id) ? 'bg-red-900/20' : ''
                              }`}
                              onClick={() => toggleDeleteRecordSelection(record.id)}
                            >
                              <TableCell className="w-[40px]">
                                <Checkbox
                                  checked={selectedDeleteRecordIds.has(record.id)}
                                  onCheckedChange={() => toggleDeleteRecordSelection(record.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              {deleteDisplayColumns.map((col) => (
                                <TableCell key={col} className="text-foreground whitespace-nowrap max-w-[180px] truncate" title={String(record[col] ?? '')}>
                                  {formatCellValue(record[col], col)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>

                  {/* Pagination */}
                  {totalDeletePages > 1 && (
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteRecordsPage(Math.max(0, deleteRecordsPage - 1))}
                        disabled={deleteRecordsPage === 0}
                        className="border-gray-600"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {deleteRecordsPage + 1} of {totalDeletePages} ({deleteRecordsData.length} records)
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteRecordsPage(Math.min(totalDeletePages - 1, deleteRecordsPage + 1))}
                        disabled={deleteRecordsPage >= totalDeletePages - 1}
                        className="border-gray-600"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteRecordsDialog(false);
                setDeleteRecordsUpload(null);
                setDeleteRecordsData([]);
                setSelectedDeleteRecordIds(new Set());
                setDeleteRecordsPage(0);
              }}
              disabled={isDeletingRecords}
              className="border-gray-600"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelectedRecords}
              disabled={selectedDeleteRecordIds.size === 0 || isDeletingRecords}
            >
              {isDeletingRecords ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedDeleteRecordIds.size} Record{selectedDeleteRecordIds.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper function to parse EFTPOS datetime (needed in component scope)
// Same logic as parseEftposDateTime inside the component - constructs ISO string directly
const parseEftposDateTimeForKey = (dateStr: string): string | null => {
  if (!dateStr || dateStr.trim() === '') return null;
  
  try {
    const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    
    if (match) {
      const [, month, day, year, hours, minutes, seconds, ampm] = match;
      let hour = parseInt(hours);
      const monthNum = parseInt(month);
      const dayNum = parseInt(day);
      const yearNum = parseInt(year);
      const minNum = parseInt(minutes);
      const sec = seconds ? parseInt(seconds) : 0;
      
      // Validate date components to avoid constructing invalid ISO strings
      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum < 1900 || yearNum > 2100) {
        return null;
      }
      
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && hour !== 12) {
          hour += 12;
        } else if (ampm.toUpperCase() === 'AM' && hour === 12) {
          hour = 0;
        }
      }
      
      // Validate time components
      if (hour < 0 || hour > 23 || minNum < 0 || minNum > 59 || sec < 0 || sec > 59) {
        return null;
      }
      
      // Construct ISO string directly WITHOUT using new Date() to avoid timezone conversion
      const isoStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minNum).padStart(2, '0')}:${String(sec).padStart(2, '0')}+00:00`;
      return isoStr;
    }
    
    // Fallback for ISO-like formats
    if (dateStr.includes('T') || dateStr.includes('+') || dateStr.endsWith('Z')) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    
    return null;
  } catch {
    return null;
  }
};



// Data Preview Table Component
const DataPreviewTable: React.FC<{
  rows: Record<string, string>[];
  headers: string[];
  duplicateKeys: Set<string>;
  previewType: 'eftpos' | 'totalmobility';
  columnOffset: number;
  visibleColumns: number;
}> = ({ rows, headers, duplicateKeys, previewType, columnOffset, visibleColumns }) => {
  
  const getDuplicateKey = (row: Record<string, string>): string => {
    if (previewType === 'eftpos') {
      const txnNumber = row.txn_number;
      const transactionDate = parseEftposDateTimeForKey(row.transaction_date);
      return `${txnNumber}_${transactionDate}`;
    } else {
      return row.operator_transaction_ref || '';
    }
  };

  const visibleHeaders = headers.slice(columnOffset, columnOffset + visibleColumns);
  const hasMoreColumns = headers.length > columnOffset + visibleColumns;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <ScrollArea className="h-[300px]">
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-yellow-500 font-bold w-16 min-w-16">#</TableHead>
                {visibleHeaders.map((header, i) => (
                  <TableHead key={i} className="text-yellow-500 font-medium whitespace-nowrap w-[120px] min-w-[120px]">
                    {header}
                  </TableHead>
                ))}
                {hasMoreColumns && (
                  <TableHead className="text-muted-foreground w-[80px] min-w-[80px]">
                    +{headers.length - columnOffset - visibleColumns} more...
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice(0, 50).map((row, rowIndex) => {
                const rowKey = getDuplicateKey(row);
                const isDuplicate = duplicateKeys.size > 0 && duplicateKeys.has(rowKey);
                return (
                  <TableRow 
                    key={rowIndex} 
                    className={`hover:bg-muted/30 ${isDuplicate ? 'bg-orange-900/20' : ''}`}
                  >
                    <TableCell className="text-muted-foreground w-16 min-w-16">
                      <div className="flex items-center gap-1">
                        {rowIndex + 1}
                        {isDuplicate && (
                          <Badge variant="outline" className="text-orange-400 border-orange-600 text-xs">
                            DUP
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    {visibleHeaders.map((header, colIndex) => (
                      <TableCell key={colIndex} className="text-foreground w-[120px] min-w-[120px] max-w-[120px] truncate" title={row[header] || '-'}>
                        {row[header] || '-'}
                      </TableCell>
                    ))}
                    {hasMoreColumns && (
                      <TableCell className="text-muted-foreground w-[80px] min-w-[80px]">...</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
      {rows.length > 50 && (
        <div className="p-2 text-center text-sm text-muted-foreground border-t border-border">
          Showing first 50 of {rows.length} rows
        </div>
      )}
      {rows.length === 0 && (
        <div className="p-8 text-center text-muted-foreground">
          No records to display
        </div>
      )}
    </div>
  );
};

export default ReportUpload;
